import { runInAction } from 'mobx';
import { useEffect, useRef } from 'react';
import { Contract, UserTradingSystemCredentials } from '@monorepo/types';
import { useStore } from '@hub/helpers/use-store';
import {
  QUERY_CONFIGS,
  QueryConfigKeys,
} from '@hub/utils/react-query/query-config';
import {
  fetchCouponMetrics,
  fetchPublicProfile,
  fetchRiseId,
  fetchUserContracts,
  fetchUserCoupons,
  fetchUserCredits,
  fetchUserCreditsHistory,
  fetchUserLogins,
  isValidUsername,
} from '@hub/utils/react-query/query-functions';
import { QUERY_KEYS } from '@hub/utils/react-query/query-keys';
import { ExtractFnReturnType } from '@hub/utils/react-query/react-query';
import { useQuery } from '@tanstack/react-query';

const CONFIG_KEY = QueryConfigKeys.USERS;

export const useUserCredits = () => {
  const {
    dataStore: { userStore },
  } = useStore();

  const userId = userStore.currentUser?.userId;
  const previousUserIdRef = useRef<string | undefined>(undefined);

  const queryKey = QUERY_KEYS[CONFIG_KEY].CREDITS(userId);

  const query = useQuery<ExtractFnReturnType<typeof fetchUserCredits>>({
    queryKey,
    queryFn: fetchUserCredits,
    staleTime: 0, // Always consider stale to ensure fresh data on userId change
    gcTime: 0, // Don't cache data - always fetch fresh (critical for impersonation)
    enabled: !!userId && userStore.isLoggedIn, // Only enable when we have a userId AND user is logged in
    // Note: refetchOnMount removed - useEffect below handles userId changes more efficiently
    refetchOnWindowFocus: false,
    meta: {
      onSuccess: (updatedCredits: number) => {
        runInAction(() => {
          if (userStore.currentUser) {
            userStore.currentUser.creditBalance = updatedCredits;
          }
        });
      },
    },
  });

  // Force refetch when userId changes (critical for impersonation)
  // This ensures we get fresh data when switching from admin to impersonated user
  // This replaces refetchOnMount to avoid double fetches when component mounts AND userId changes
  useEffect(() => {
    const previousUserId = previousUserIdRef.current;

    if (userId && previousUserId !== undefined && previousUserId !== userId) {
      query.refetch();
    }

    previousUserIdRef.current = userId;
  }, [userId, query]);

  return query;
};

export const useUserCreditsHistory = () => {
  return useQuery<ExtractFnReturnType<typeof fetchUserCreditsHistory>>({
    queryKey: QUERY_KEYS[CONFIG_KEY].CREDITS_HISTORY,
    queryFn: fetchUserCreditsHistory,
    staleTime: QUERY_CONFIGS[CONFIG_KEY].staleTime,
  });
};

export const useUserLogins = () => {
  const {
    dataStore: { userStore },
  } = useStore();

  return useQuery<ExtractFnReturnType<typeof fetchUserLogins>>({
    queryKey: QUERY_KEYS[CONFIG_KEY].LOGINS,
    queryFn: fetchUserLogins,
    staleTime: QUERY_CONFIGS[CONFIG_KEY].staleTime,
    meta: {
      onSuccess: (logins: UserTradingSystemCredentials[]) => {
        runInAction(() => {
          userStore.currentUser.tsUsers = logins;
        });
      },
    },
  });
};

export const useUserContracts = () => {
  const {
    dataStore: { contractStore },
  } = useStore();

  return useQuery<ExtractFnReturnType<typeof fetchUserContracts>>({
    queryKey: QUERY_KEYS[CONFIG_KEY].CONTRACTS,
    queryFn: fetchUserContracts,
    staleTime: QUERY_CONFIGS[CONFIG_KEY].staleTime,
    meta: {
      onSuccess: (contracts: Contract[]) => {
        runInAction(() => {
          contractStore.contracts = contracts;
        });
      },
    },
  });
};

export const useGetRiseId = () => {
  return useQuery<ExtractFnReturnType<typeof fetchRiseId>>({
    queryKey: QUERY_KEYS[CONFIG_KEY].RISE_ID,
    queryFn: fetchRiseId,
    staleTime: QUERY_CONFIGS[CONFIG_KEY].staleTime,
  });
};

export const useGetPublicProfile = (publicId?: string) => {
  return useQuery<ExtractFnReturnType<typeof fetchPublicProfile>>({
    queryKey: QUERY_KEYS[CONFIG_KEY].PUBLIC_PROFILE(publicId),
    queryFn: () => fetchPublicProfile(publicId),
    staleTime: QUERY_CONFIGS[CONFIG_KEY].staleTime,
  });
};

export const useValidUsername = (username: string) => {
  return useQuery<ExtractFnReturnType<typeof isValidUsername>>({
    queryKey: QUERY_KEYS[CONFIG_KEY].VALID_USERNAME(username),
    queryFn: () => isValidUsername(username),
    staleTime: QUERY_CONFIGS[CONFIG_KEY].staleTime,
    enabled: !!username,
  });
};

export const useUserCoupons = () => {
  return useQuery<ExtractFnReturnType<typeof fetchUserCoupons>>({
    queryKey: QUERY_KEYS[CONFIG_KEY].COUPONS,
    queryFn: fetchUserCoupons,
    staleTime: QUERY_CONFIGS[CONFIG_KEY].staleTime,
  });
};

export const useCouponMetrics = () => {
  return useQuery<ExtractFnReturnType<typeof fetchCouponMetrics>>({
    queryKey: QUERY_KEYS[CONFIG_KEY].COUPON_METRICS,
    queryFn: fetchCouponMetrics,
    staleTime: QUERY_CONFIGS[CONFIG_KEY].staleTime,
  });
};
